import { DndContext, KeyboardSensor, PointerSensor, pointerWithin, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { Columns3, KanbanSquare, Plus, Search } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAccessProjects } from '@/features/access/api'
import { useCustomers } from '@/features/crm/api'
import { apiErrorMessage } from '@/i18n'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { useStaffLookup } from '../ui'
import { type Board, type BoardColumn, type BoardTask, useArchiveBoardTask, useBoards, useBoardTasks, useMoveBoardTask } from './api'
import { BoardColumnsModal } from './BoardColumnsModal'
import { isBoardTaskOverdue } from './board'
import { BoardTaskCard } from './BoardTaskCard'
import { BoardTaskModal } from './BoardTaskModal'

const ME = '__me__'
const UNASSIGNED = '__none__'

/**
 * Ekip panoları (Teknik, Satış, Destek…): kuralların ve diğer modüllerin açtığı görevler, projeye bağlı olsun ya da
 * olmasın, panonun kolonlarında izlenir ve sürüklenerek ilerletilir.
 */
export function TaskBoardPage() {
  const { t } = useTranslation()
  const boards = useBoards()
  const [params, setParams] = useSearchParams()
  const list = boards.data ?? []
  const board = list.find((b) => b.code === params.get('board')) ?? list.find((b) => b.isDefault) ?? list[0]

  return (
    <>
      <PageHeader eyebrow={t('nav.groups.projects')} title={t('projects.board.title')} description={t('projects.board.subtitle')} />
      {boards.isError ? (
        <ErrorState error={boards.error} onRetry={() => void boards.refetch()} />
      ) : boards.isLoading ? (
        <Skeleton className="h-[28rem] rounded-xl" />
      ) : !board ? (
        <EmptyState icon={KanbanSquare} title={t('projects.board.noBoards')} />
      ) : (
        <BoardView
          key={board.id}
          board={board}
          boards={list}
          onBoard={(code) =>
            setParams(
              (prev) => {
                const out = new URLSearchParams(prev)
                out.set('board', code)
                return out
              },
              { replace: true },
            )
          }
        />
      )}
    </>
  )
}

function BoardView({ board, boards, onBoard }: { board: Board; boards: Board[]; onBoard: (code: string) => void }) {
  const { t } = useTranslation()
  const { can, user } = useAuth()
  const navigate = useNavigate()
  const tasks = useBoardTasks(board.id)
  const move = useMoveBoardTask(board.id)
  const archive = useArchiveBoardTask()
  const { staff, nameOf } = useStaffLookup()
  const customers = useCustomers()
  const projects = useAccessProjects()
  const [search, setSearch] = useState('')
  const [assignee, setAssignee] = useState('')
  const [priority, setPriority] = useState('')
  const [editing, setEditing] = useState<{ task: BoardTask | null; column?: string } | null>(null)
  const [columnsOpen, setColumnsOpen] = useState(false)
  const canWrite = can('tasks.write')
  const canManage = can('tasks.boards.manage')
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor))

  const customerName = useMemo(() => new Map((customers.data ?? []).map((c) => [c.id, c.name])), [customers.data])
  const projectName = useMemo(() => new Map((projects.data ?? []).map((p) => [p.id, p.name])), [projects.data])
  const people = useMemo(() => staff.map((s) => ({ value: s.id, label: s.name, description: s.position, keywords: [s.name] })), [staff])
  const customerOptions = useMemo(() => (customers.data ?? []).map((c) => ({ value: c.id, label: c.name })), [customers.data])
  const projectOptions = useMemo(() => (projects.data ?? []).map((p) => ({ value: p.id, label: p.name })), [projects.data])

  const all = useMemo(() => tasks.data ?? [], [tasks.data])
  const visible = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr')
    return all.filter((task) => {
      if (assignee === ME && task.assigneeId !== user?.id) return false
      if (assignee === UNASSIGNED && task.assigneeId) return false
      if (assignee && assignee !== ME && assignee !== UNASSIGNED && task.assigneeId !== assignee) return false
      if (priority && task.priority !== priority) return false
      if (!q) return true
      const hay = [task.title, task.description, task.key, customerName.get(task.accountId), projectName.get(task.projectId)].join(' ').toLocaleLowerCase('tr')
      return hay.includes(q)
    })
  }, [all, search, assignee, priority, user?.id, customerName, projectName])

  const byColumn = useMemo(() => {
    const map = new Map<string, BoardTask[]>(board.columns.map((c) => [c.key, []]))
    for (const task of visible) map.get(task.columnKey)?.push(task)
    return map
  }, [board.columns, visible])
  const counts = useMemo(() => {
    const out: Record<string, number> = {}
    for (const task of all) out[task.columnKey] = (out[task.columnKey] ?? 0) + 1
    return out
  }, [all])
  const doneKeys = new Set(board.columns.filter((c) => c.isDone).map((c) => c.key))
  const overdue = all.filter((task) => isBoardTaskOverdue(task, doneKeys.has(task.columnKey))).length

  const onMove = (task: BoardTask, column: string) => {
    if (task.columnKey === column) return
    move.mutate({ taskId: task.id, columnKey: column }, { onError: (e) => toast.error(apiErrorMessage(e)) })
  }
  const onDragEnd = (e: DragEndEvent) => {
    const task = all.find((x) => x.id === e.active.id)
    if (task && e.over) onMove(task, String(e.over.id))
  }
  const onOpen = (task: BoardTask) => {
    if (task.projectId) navigate(`/projects/${task.projectId}/tasks/${task.id}`)
    else setEditing({ task })
  }
  const onArchive = async (task: BoardTask) => {
    if (!(await confirmModal({ title: t('projects.board.archiveTitle'), description: task.title, confirmLabel: t('projects.board.archive') }))) return
    archive.mutate(task.id, { onSuccess: () => toast.success(t('projects.board.archived')), onError: (e) => toast.error(apiErrorMessage(e)) })
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Tabs value={board.code} onValueChange={onBoard} className="min-w-0 max-w-full">
          <TabsList>
            {boards.map((b) => (
              <TabsTrigger key={b.id} value={b.code}>
                {b.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <div className="ml-auto flex items-center gap-2">
          {canManage ? (
            <Button variant="outline" size="sm" onClick={() => setColumnsOpen(true)}>
              <Columns3 />
              <span className="hidden sm:inline">{t('projects.board.columns.open')}</span>
            </Button>
          ) : null}
          {canWrite ? (
            <Button size="sm" onClick={() => setEditing({ task: null })}>
              <Plus />
              {t('projects.board.add')}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 basis-56">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('projects.board.search')} aria-label={t('projects.board.search')} className="pl-9" />
        </div>
        <Select
          className="w-full sm:w-56"
          searchable
          value={assignee}
          onChange={setAssignee}
          aria-label={t('projects.board.fields.assignee')}
          options={[
            { value: '', label: t('projects.board.filters.everyone') },
            { value: ME, label: t('projects.board.filters.mine') },
            { value: UNASSIGNED, label: t('projects.work.unassigned') },
            ...people,
          ]}
        />
        <Select
          className="w-full sm:w-40"
          value={priority}
          onChange={setPriority}
          aria-label={t('projects.board.fields.priority')}
          options={[{ value: '', label: t('projects.board.filters.anyPriority') }, ...(['urgent', 'high', 'normal', 'low'] as const).map((p) => ({ value: p, label: t(`projects.rules.priority.${p}`) }))]}
        />
        <p className="w-full text-xs text-muted-foreground sm:ml-auto sm:w-auto">
          {t('projects.board.summary', { count: all.length })}
          {overdue ? <span className="font-medium text-destructive"> · {t('projects.board.overdue', { count: overdue })}</span> : null}
        </p>
      </div>

      {board.description ? <p className="text-sm text-muted-foreground">{board.description}</p> : null}

      {tasks.isError ? (
        <ErrorState error={tasks.error} onRetry={() => void tasks.refetch()} />
      ) : tasks.isLoading ? (
        <Skeleton className="h-[24rem] rounded-xl" />
      ) : (
        <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragEnd={onDragEnd}>
          <div className="scrollbar-none -mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-3 sm:mx-0 sm:px-0">
            {board.columns.map((col, i) => {
              const items = byColumn.get(col.key) ?? []
              return (
                <Column key={col.key} column={col} count={counts[col.key] ?? 0}>
                  {items.map((task) => (
                    <BoardTaskCard
                      key={task.id}
                      task={task}
                      prev={board.columns[i - 1]}
                      next={board.columns[i + 1]}
                      done={col.isDone}
                      assignee={task.assigneeId ? nameOf(task.assigneeId) : null}
                      customer={customerName.get(task.accountId)}
                      project={projectName.get(task.projectId)}
                      canWrite={canWrite}
                      onOpen={onOpen}
                      onEdit={(x) => setEditing({ task: x })}
                      onMove={onMove}
                      onArchive={(x) => void onArchive(x)}
                    />
                  ))}
                  {items.length === 0 ? <p className="px-2 py-6 text-center text-xs text-muted-foreground">{t('projects.kanban.emptyColumn')}</p> : null}
                  {canWrite && i === 0 ? (
                    <button
                      type="button"
                      onClick={() => setEditing({ task: null, column: col.key })}
                      className="flex items-center justify-center gap-2 rounded-xl border border-dashed bg-card/60 py-3 text-sm font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Plus className="size-4" aria-hidden />
                      {t('projects.board.add')}
                    </button>
                  ) : null}
                </Column>
              )
            })}
          </div>
        </DndContext>
      )}

      {editing ? (
        <BoardTaskModal
          board={board}
          task={editing.task}
          defaultColumn={editing.column}
          people={people}
          customers={customerOptions}
          projects={projectOptions}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {columnsOpen ? <BoardColumnsModal board={board} counts={counts} onClose={() => setColumnsOpen(false)} /> : null}
    </div>
  )
}

function Column({ column, count, children }: { column: BoardColumn; count: number; children: ReactNode }) {
  const { t } = useTranslation()
  const { setNodeRef, isOver } = useDroppable({ id: column.key })
  const overLimit = column.wipLimit != null && count > column.wipLimit
  return (
    <section
      ref={setNodeRef}
      aria-label={column.name}
      className={cn(
        'flex w-[17.5rem] shrink-0 snap-start flex-col rounded-xl border p-2 transition-colors sm:w-72',
        column.isDone ? 'bg-success/[0.06]' : 'bg-muted/40',
        isOver && 'ring-2 ring-primary/40',
      )}
    >
      <header className="flex items-center gap-2 px-1.5 pb-2 pt-1">
        <span className={cn('size-2.5 rounded-full', column.isDone ? 'bg-success' : 'bg-muted-foreground/60')} aria-hidden />
        <h3 className="min-w-0 truncate text-sm font-semibold">{column.name}</h3>
        <span
          className={cn('ml-auto rounded-full bg-card px-2 text-xs font-semibold tabular-nums text-muted-foreground shadow-xs', overLimit && 'bg-destructive/10 text-destructive')}
          title={column.wipLimit != null ? t('projects.board.wipTitle', { limit: column.wipLimit }) : undefined}
        >
          {column.wipLimit != null ? `${count}/${column.wipLimit}` : count}
        </span>
      </header>
      <div className="flex min-h-24 flex-1 flex-col gap-2">{children}</div>
    </section>
  )
}
